/**
 * De kaart van het zoekgebied: de CBS-buurten als vlakken. Klik zet een buurt
 * aan of uit; aan is gevuld in de accentkleur. Een buurt die niet aan staat
 * maar waarvan alle postcodes toch al doorzocht worden krijgt een vleugje
 * accent: die zoekt de ronde vanzelf mee, want Funda zoekt op postcode.
 *
 * Aan, mee en onder de muis zijn feature-state, geen nieuwe data: een klik
 * tekent alleen dat ene vlak opnieuw in plaats van alle vijfhonderd.
 */
import type { BuurtenKaart } from '@mamimap/shared';
import {
  type ExpressionSpecification,
  type FillLayerSpecification,
  type GeoJSONSourceSpecification,
  type LineLayerSpecification,
  type LngLatBoundsLike,
  Map as MapLibreMap,
  NavigationControl,
} from 'maplibre-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { kaartstijl } from '../../map/kaartstijlen';

const BRON = 'buurten';
const VLAK = 'buurten-vlak';
const RAND = 'buurten-rand';

/* Dezelfde waarden als --color-accent, --color-accent-deep en --color-faint in
   index.css; MapLibre leest geen CSS-variabelen. */
const ACCENT = '#1d4e63';
const ACCENT_DIEP = '#133748';
const INKT = '#4a4842';
const RANDKLEUR = '#8a867a';

type Info = { buurtnaam: string; wijknaam: string; postcodes: string[] };

type ZoekgebiedKaartProps = {
  kaart: BuurtenKaart;
  gekozen: ReadonlySet<string>;
  /** Niet gekozen, maar al zijn postcodes zitten in het plan. */
  meegezocht: ReadonlySet<string>;
  onWissel: (buurtcode: string) => void;
};

export function ZoekgebiedKaart({ kaart, gekozen, meegezocht, onWissel }: ZoekgebiedKaartProps) {
  const houder = useRef<HTMLDivElement>(null);
  const kaartRef = useRef<MapLibreMap | null>(null);
  const wisselRef = useRef(onWissel);
  wisselRef.current = onWissel;
  const [geladen, zetGeladen] = useState(false);
  const [info, zetInfo] = useState<Info | null>(null);

  const idPerCode = useMemo(
    () => new Map(kaart.features.map((feature) => [feature.properties.buurtcode, feature.id])),
    [kaart],
  );

  useEffect(() => {
    if (!houder.current) return;
    const map = new MapLibreMap({
      container: houder.current,
      style: kaartstijl('grijs'),
      bounds: omhullende(kaart),
      fitBoundsOptions: { padding: 16 },
      attributionControl: { compact: false },
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
    kaartRef.current = map;

    const aan: ExpressionSpecification = ['boolean', ['feature-state', 'aan'], false];
    const mee: ExpressionSpecification = ['boolean', ['feature-state', 'mee'], false];
    const onder: ExpressionSpecification = ['boolean', ['feature-state', 'onder'], false];

    map.on('load', () => {
      map.addSource(BRON, {
        type: 'geojson',
        data: kaart as unknown as GeoJSONSourceSpecification['data'],
      });
      const vlak: FillLayerSpecification = {
        id: VLAK,
        type: 'fill',
        source: BRON,
        paint: {
          'fill-color': ['case', aan, ACCENT, mee, ACCENT, INKT],
          'fill-opacity': ['case', aan, 0.55, mee, 0.16, onder, 0.1, 0.01],
        },
      };
      map.addLayer(vlak);
      const rand: LineLayerSpecification = {
        id: RAND,
        type: 'line',
        source: BRON,
        paint: {
          'line-color': ['case', aan, ACCENT_DIEP, RANDKLEUR],
          'line-width': ['case', onder, 2, aan, 1, 0.6],
          'line-opacity': 0.85,
        },
      };
      map.addLayer(rand);
      zetGeladen(true);
    });

    let onderId: number | null = null;
    const zetOnder = (id: number | null) => {
      if (onderId === id) return;
      if (onderId !== null) map.setFeatureState({ source: BRON, id: onderId }, { onder: false });
      onderId = id;
      if (id !== null) map.setFeatureState({ source: BRON, id }, { onder: true });
    };

    map.on('mousemove', VLAK, (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      map.getCanvas().style.cursor = 'pointer';
      zetOnder(typeof feature.id === 'number' ? feature.id : null);
      const p = feature.properties as Record<string, unknown>;
      zetInfo({
        buurtnaam: String(p.buurtnaam ?? ''),
        wijknaam: String(p.wijknaam ?? ''),
        postcodes: leesPostcodes(p.postcodes),
      });
    });
    map.on('mouseleave', VLAK, () => {
      map.getCanvas().style.cursor = '';
      zetOnder(null);
      zetInfo(null);
    });
    map.on('click', VLAK, (event) => {
      const code = event.features?.[0]?.properties?.buurtcode;
      if (typeof code === 'string') wisselRef.current(code);
    });

    return () => {
      kaartRef.current = null;
      zetGeladen(false);
      map.remove();
    };
  }, [kaart]);

  useEffect(() => {
    const map = kaartRef.current;
    if (!map || !geladen) return;
    for (const [code, id] of idPerCode) {
      map.setFeatureState(
        { source: BRON, id },
        { aan: gekozen.has(code), mee: !gekozen.has(code) && meegezocht.has(code) },
      );
    }
  }, [geladen, gekozen, meegezocht, idPerCode]);

  return (
    <div className="relative h-[460px] border border-line bg-paper">
      <div ref={houder} className="absolute inset-0" />
      {info ? (
        <div className="pointer-events-none absolute top-2 left-2 border border-line bg-surface-strong px-2 py-1 text-[12px]">
          <div className="text-ink">{info.buurtnaam}</div>
          <div className="text-[11.5px] text-muted">
            {info.wijknaam}
            {info.postcodes.length > 0 ? (
              <span className="cijfers"> · {info.postcodes.join(', ')}</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** MapLibre geeft arrays in properties terug als JSON-tekst. */
function leesPostcodes(waarde: unknown): string[] {
  if (Array.isArray(waarde)) return waarde.map(String);
  if (typeof waarde === 'string') {
    try {
      const lijst: unknown = JSON.parse(waarde);
      if (Array.isArray(lijst)) return lijst.map(String);
    } catch {
      return [];
    }
  }
  return [];
}

function omhullende(kaart: BuurtenKaart): LngLatBoundsLike {
  let minX = 180;
  let minY = 90;
  let maxX = -180;
  let maxY = -90;
  const loop = (waarde: unknown): void => {
    if (!Array.isArray(waarde)) return;
    if (typeof waarde[0] === 'number' && typeof waarde[1] === 'number') {
      const [x, y] = waarde as [number, number];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    for (const deel of waarde) loop(deel);
  };
  for (const feature of kaart.features) loop(feature.geometry.coordinates);
  if (minX > maxX) return [4.73, 52.28, 5.07, 52.43];
  return [minX, minY, maxX, maxY];
}
